import { useState } from 'react';
import { useVisualizerStore } from '@/stores/useVisualizerStore';
import type { Finish, PatternId } from '@/types';
import rawFinishes from '../data/finishes.json';

interface RawFinish {
  id: string;
  name: string;
  category: string;
  patternId: string;
  hex: string;
  labL: number;
  labA: number;
  labB: number;
  isPremium: boolean;
}

const FINISHES: Finish[] = (rawFinishes as unknown as RawFinish[]).map((f) => ({
  id: f.id,
  name: f.name,
  category: f.category as Finish['category'],
  patternId: (f.patternId ?? 'solid') as PatternId,
  color: f.hex,
  labL: f.labL,
  labA: f.labA,
  labB: f.labB,
  glossFactors: {
    matte: { lightness: 0, saturation: 0 },
    satin: { lightness: 0, saturation: 0 },
    high: { lightness: 0, saturation: 0 },
  },
  isPremium: f.isPremium,
}));

const CATEGORY_LABELS: Record<string, string> = {
  all: 'All',
  white: 'White & Off-Whites',
  beige: 'Beige & Neutrals',
  gray: 'Gray Tones',
  color: 'Bold Colors',
  speckle: 'Textured Finishes',
  custom: 'Custom',
};

const SPECKLE_DOT_COLORS: Record<PatternId, string> = {
  'solid': '',
  'fine-speckle': '#6b7280',
  'medium-speckle': '#4b5563',
  'coarse-speckle': '#374151',
};

interface ColorSwatchesProps {
  selectedFinish?: Finish;
  onSelectFinish?: (finish: Finish) => void;
}

export function ColorSwatches({ selectedFinish: controlledFinish, onSelectFinish }: ColorSwatchesProps = {}) {
  const storeSelectedFinish = useVisualizerStore((s) => s.selectedFinish);
  const setFinish = useVisualizerStore((s) => s.setFinish);
  const selectedFinish = controlledFinish ?? storeSelectedFinish;
  const handleSelectFinish = onSelectFinish ?? setFinish;
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const colorCategories = Array.from(new Set(FINISHES.map((f) => f.category)));
  const categories = ['all', ...colorCategories, 'speckle'];

  const filtered =
    activeCategory === 'speckle'
      ? FINISHES.filter((f) => f.patternId !== 'solid')
      : activeCategory === 'all'
      ? FINISHES
      : FINISHES.filter((f) => f.category === activeCategory);

  return (
    <div>
      {/* Category tabs */}
      <div className="scrollbar-hide mb-2 flex gap-1.5 overflow-x-auto">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold transition-all ${
              activeCategory === cat
                ? cat === 'speckle'
                  ? 'bg-violet-600 text-white shadow-md shadow-violet-600/20'
                  : 'bg-bss-blue text-white shadow-md shadow-blue-600/20'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
            }`}
          >
            {CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
      </div>

      {/* Swatches */}
      <div className="scrollbar-hide flex gap-2 overflow-x-auto py-1.5">
        {filtered.map((finish) => {
          const isSpeckle = finish.patternId !== 'solid';
          const isSelected = selectedFinish.id === finish.id;
          return (
            <button
              key={finish.id}
              onClick={() => handleSelectFinish(finish)}
              className={`group relative flex shrink-0 flex-col items-center gap-1 rounded-xl p-1.5 transition-all duration-200 preserve-3d ${
                isSelected ? 'bg-blue-50 shadow-glow-blue-sm' : 'hover:bg-slate-50'
              }`}
              title={finish.name}
            >
              {/* Color orb with orbit ring when selected */}
              <div className="relative">
                <div
                  className={`h-9 w-9 rounded-full border-2 transition-all duration-300 preserve-3d ${
                    isSelected
                      ? 'border-bss-blue shadow-lg shadow-blue-600/20 scale-110'
                      : 'border-slate-200 shadow-sm group-hover:scale-105'
                  }`}
                  style={{
                    backgroundColor: finish.color,
                    boxShadow: isSelected
                      ? `0 4px 16px ${finish.color}66, 0 0 0 3px rgba(0, 102, 204, 0.15)`
                      : undefined,
                  }}
                />
                {isSelected && (
                  <div className="absolute -inset-1 rounded-full border-2 border-bss-blue/30 animate-spin" style={{ animationDuration: '3s' }} />
                )}
                {/* Speckle badge — small dotted overlay in bottom-right */}
                {isSpeckle && (
                  <div
                    className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border border-white flex items-center justify-center"
                    style={{ backgroundColor: SPECKLE_DOT_COLORS[finish.patternId] }}
                    title={finish.patternId}
                  >
                    <svg viewBox="0 0 8 8" className="h-2 w-2 fill-white opacity-90">
                      <circle cx="2" cy="2" r="0.9" />
                      <circle cx="6" cy="3" r="0.7" />
                      <circle cx="3" cy="6" r="0.8" />
                      <circle cx="6.5" cy="6.5" r="0.6" />
                    </svg>
                  </div>
                )}
              </div>
              <span
                className={`max-w-[4rem] truncate text-[9px] font-bold ${
                  isSelected ? 'text-bss-blue' : 'text-slate-500'
                }`}
              >
                {finish.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
